import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { UF_LIST } from "@/lib/constants";

const empty = {
  equipment_type: "", application: "", capacity: "", temperature: "", dimensions: "",
  name: "", company: "", phone: "", email: "", city: "", state: "", message: "", product_slug: "", source: "site",
};

export default function QuoteDialog({ open, onOpenChange, prefill = {}, categories = [] }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, ...prefill });
      setStep(prefill.equipment_type ? 2 : 1);
    }
  }, [open, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.phone) {
      toast.error("Informe ao menos nome e telefone.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leads", form);
      setStep(4);
      if (window.fbq) window.fbq("track", "Lead");
      if (window.gtag) window.gtag("event", "generate_lead");
    } catch (e) {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const equipOptions = categories.length
    ? categories.map((c) => c.name)
    : ["Chiller", "Unidade Condensadora", "Bomba de Calor", "Torre de Resfriamento", "Câmara Fria", "Equipamento de Sorvete", "Túnel de Congelamento", "Contêiner Refrigerado", "Outro"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-[#27272A] bg-[#151618] text-white rounded-none" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {step === 4 ? "Pedido enviado!" : "Solicitar Orçamento / Dimensionamento"}
          </DialogTitle>
        </DialogHeader>

        {step < 4 && (
          <div className="mb-4 flex gap-1.5">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`h-1 flex-1 ${step >= n ? "bg-[#FF5500]" : "bg-[#27272A]"}`} />
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-[#A1A1AA]">Qual equipamento você procura?</p>
            <div className="grid max-h-[50vh] grid-cols-1 gap-2 overflow-y-auto pr-1">
              {equipOptions.map((opt) => (
                <button
                  key={opt}
                  data-testid={`quote-equip-${opt}`}
                  onClick={() => { set("equipment_type", opt); setStep(2); }}
                  className="border border-[#27272A] bg-[#0A0A0B] px-4 py-3 text-left text-sm font-medium transition-colors duration-200 hover:border-[#FF5500] hover:bg-[#1E1F22]"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-[#A1A1AA]">Detalhes técnicos ({form.equipment_type}). Preencha o que souber.</p>
            <div>
              <Label className="label-eyebrow">Aplicação / Processo</Label>
              <Input data-testid="quote-application" value={form.application} onChange={(e) => set("application", e.target.value)} placeholder="Ex.: injetora, câmara fria, piscina..." className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Capacidade</Label>
                <Input data-testid="quote-capacity" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="Ex.: TR, HP, kcal" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
              </div>
              <div>
                <Label className="label-eyebrow">Temperatura</Label>
                <Input data-testid="quote-temperature" value={form.temperature} onChange={(e) => set("temperature", e.target.value)} placeholder="Ex.: -18°C" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Dimensões / Volume</Label>
              <Input data-testid="quote-dimensions" value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} placeholder="Ex.: câmara 4x5x3m" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
            </div>
            <div>
              <Label className="label-eyebrow">Mensagem</Label>
              <Textarea data-testid="quote-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Descreva sua necessidade" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(1)} className="rounded-none">Voltar</Button>
              <Button data-testid="quote-next-2" onClick={() => setStep(3)} className="rounded-none bg-[#FF5500] hover:bg-[#CC4400]">Continuar</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <p className="text-sm text-[#A1A1AA]">Seus dados de contato</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Nome *</Label>
                <Input data-testid="quote-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
              </div>
              <div>
                <Label className="label-eyebrow">Empresa</Label>
                <Input data-testid="quote-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Telefone / WhatsApp *</Label>
                <Input data-testid="quote-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(47) 9....." className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
              </div>
              <div>
                <Label className="label-eyebrow">E-mail</Label>
                <Input data-testid="quote-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Cidade</Label>
                <Input data-testid="quote-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" />
              </div>
              <div>
                <Label className="label-eyebrow">UF</Label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="quote-state" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]"><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent className="bg-[#151618] text-white border-[#27272A]">
                    {UF_LIST.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(2)} className="rounded-none">Voltar</Button>
              <Button data-testid="quote-submit-button" onClick={submit} disabled={saving} className="rounded-none bg-[#FF5500] hover:bg-[#CC4400]">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enviar pedido
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="py-6 text-center" data-testid="quote-success">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center bg-[#FF5500]"><Check className="h-8 w-8 text-white" /></div>
            <p className="text-[#A1A1AA]">Recebemos sua solicitação. Nossa engenharia vai analisar e retornar em breve com o dimensionamento e a proposta.</p>
            <Button onClick={() => onOpenChange(false)} className="mt-6 rounded-none bg-[#FF5500] hover:bg-[#CC4400]">Fechar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
